import { isPlainObject } from 'es-toolkit/predicate';
import { isNonEmptyString } from '@/lib/utils/type-guards';
import type { ToolExecutionState } from './types';

const readRecordValue = (record: Record<string, unknown>, key: string): unknown => {
  const descriptor = Object.getOwnPropertyDescriptor(record, key);
  return descriptor?.value;
};

export const getErrorMessage = (result: unknown): string | null => {
  if (!isPlainObject(result)) {
    return null;
  }
  const message = readRecordValue(result, 'error');
  return typeof message === 'string' ? message : null;
};

export const formatLabel = (label: string): string => {
  const withSpaces = label
    .replaceAll(/[_-]+/g, ' ')
    .replaceAll(/([a-z\d])([A-Z])/g, '$1 $2')
    .trim();
  if (!withSpaces) {
    return 'Value';
  }
  return withSpaces.charAt(0).toUpperCase() + withSpaces.slice(1);
};

const truncate = (value: string, limit = 160): string => (value.length > limit ? `${value.slice(0, limit)}…` : value);

export const formatDisplayValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'string') {
    return truncate(value);
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      return 'Empty list';
    }
    const preview = value.slice(0, 3).map((item) => formatDisplayValue(item));
    return value.length > 3 ? `${preview.join(', ')}, … (${value.length} items)` : preview.join(', ');
  }
  if (isPlainObject(value)) {
    const entries = Object.entries(value).slice(0, 3);
    if (entries.length === 0) {
      return 'Empty object';
    }
    const parts = entries.map(([key, value_]) => `${formatLabel(key)}: ${formatDisplayValue(value_)}`);
    return entries.length < Object.keys(value).length ? `${parts.join(', ')}, …` : parts.join(', ');
  }
  if (value instanceof Date && Number.isFinite(value.valueOf())) {
    return value.toISOString();
  }
  if (typeof value === 'bigint') {
    return value.toString();
  }
  if (typeof value === 'symbol') {
    return value.description ? `Symbol(${value.description})` : 'Symbol';
  }
  if (typeof value === 'function') {
    return value.name ? `[Function ${value.name}]` : '[Function]';
  }
  try {
    return JSON.stringify(value);
  } catch {
    return '[unserializable]';
  }
};

const preferredRecordKeys = ['id', 'key', 'slug', 'username', 'name'];

export const extractPreferredKey = (record: Record<string, unknown>): string | null => {
  for (const key of preferredRecordKeys) {
    const value = readRecordValue(record, key);
    if (isNonEmptyString(value)) {
      const normalized = value.trim();
      return `${key}-${normalized}`;
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      return `${key}-${value}`;
    }
  }
  return null;
};

export const getArrayItemKey = (item: unknown, index: number): string => {
  if (isPlainObject(item)) {
    const candidate = extractPreferredKey(item);
    if (candidate) {
      return candidate;
    }
  }
  if (typeof item === 'string' || typeof item === 'number' || typeof item === 'boolean') {
    return `${typeof item}-${String(item)}`;
  }
  if (Array.isArray(item)) {
    return `array-${item.length}-${index}`;
  }
  try {
    return `json-${JSON.stringify(item)}`;
  } catch {
    return `fallback-${index}`;
  }
};

export const extractQueryArg = (arguments_: Record<string, unknown>): string | null => {
  const query = readRecordValue(arguments_, 'query');
  if (isNonEmptyString(query)) {
    return query.trim();
  }
  if (typeof query === 'number' && Number.isFinite(query)) {
    return String(query);
  }
  return null;
};

export const pluralize = (count: number, noun: string): string =>
  count === 1 ? `${count} ${noun}` : `${count} ${noun}s`;

export interface ToolRenderState {
  emptyStateMessage: string | null;
  errorMessage: string | null;
  hasResult: boolean;
  status: 'loading' | 'success' | 'error' | 'idle';
}

export const buildToolRenderState = (state: ToolExecutionState, result: unknown): ToolRenderState => {
  const errorMessage = getErrorMessage(result);
  const hasResult = result !== undefined && result !== null;
  const emptyStateMessage =
    state === 'result' && !hasResult && !errorMessage ? 'The tool completed but did not return any data.' : null;

  if (state === 'partial-call' || state === 'call') {
    return { status: 'loading', errorMessage, hasResult, emptyStateMessage };
  }

  if (state === 'result' && errorMessage) {
    return { status: 'error', errorMessage, hasResult, emptyStateMessage };
  }

  if (state === 'result' && !errorMessage) {
    return { status: 'success', errorMessage, hasResult, emptyStateMessage };
  }

  return { status: 'idle', errorMessage, hasResult, emptyStateMessage };
};
